/**
 * Outbox worker (R7.2).
 *
 * Processes the outbox queue sequentially.
 *
 * CONCURRENCY RULE:
 *   The `isProcessing` flag in OutboxStore acts as a mutex. Before starting a
 *   sync run, the worker checks and sets this flag. If it is already set, the
 *   run is aborted immediately. This prevents double-submission if:
 *     - The user manually clicks "Retry" while auto-sync is running.
 *     - The network comes back online while a manual retry is in progress.
 *
 * The worker is triggered:
 *   1. On app start (via `useEffect` in `OutboxWorkerBoot`).
 *   2. Every time the network transitions from offline → online.
 *   3. When the user clicks "Retry now" on the Outbox page.
 */

import type { LocalMediaRef, OutboxItem, PhotoPayload, AudioPayload } from './outboxTypes';
import { filesApi } from '../api/files';
import { timelineApi } from '../api/timeline';
import { base64ToFile, readMediaFile } from '../platform/filesystem';

/**
 * R-PERF W4-B8: Resolve a LocalMediaRef to a File for upload.
 * Supports both new filePath-based refs and legacy base64 dataUrl refs.
 */
async function resolveMediaRef(ref: LocalMediaRef): Promise<File> {
  if (ref.filePath) {
    return readMediaFile(ref.filePath, ref.mimeType, ref.filename);
  }
  // Legacy backward compat: dataUrl stored in JSON
  return base64ToFile(ref.dataUrl!, ref.filename);
}

// ─── Item processor ───────────────────────────────────────────────────────────

async function processItem(item: OutboxItem): Promise<void> {
  switch (item.type) {
    case 'TIMELINE_TEXT_CREATE': {
      const p = item.payload as { worksiteId: string; textContent: string };
      await timelineApi.createText(p.worksiteId, { textContent: p.textContent });
      break;
    }

    case 'TIMELINE_PHOTO_CREATE': {
      const p = item.payload as PhotoPayload;
      const fileAssetIds: string[] = [];
      for (const photo of p.photos) {
        const file = await resolveMediaRef(photo);
        const res = await filesApi.upload(file);
        fileAssetIds.push(res.fileAssetId);
      }
      await timelineApi.createPhoto(p.worksiteId, {
        fileAssetIds,
        caption: p.caption ?? null,
      });
      break;
    }

    case 'TIMELINE_AUDIO_CREATE': {
      const p = item.payload as AudioPayload;
      const file = await resolveMediaRef(p.audio);
      const res = await filesApi.upload(file);
      await timelineApi.createAudio(p.worksiteId, {
        fileAssetId: res.fileAssetId,
        caption: p.caption ?? null,
      });
      break;
    }

    default:
      throw new Error(`Unknown outbox item type: ${(item as OutboxItem).type}`);
  }
}

// ─── Worker callbacks (injected from OutboxWorkerBoot) ────────────────────────

export interface WorkerCallbacks {
  getItems: () => OutboxItem[];
  isProcessing: () => boolean;
  setProcessing: (v: boolean) => void;
  setStatus: (id: string, status: 'SYNCING' | 'DONE' | 'FAILED', error?: string) => void;
  removeItem: (id: string) => void;
}

/**
 * Run one full pass through the queue — processes PENDING and FAILED items
 * sequentially until all are either DONE or FAILED.
 *
 * Returns immediately if `isProcessing()` returns true (mutex guard).
 */
export async function runOutboxWorker(cb: WorkerCallbacks): Promise<void> {
  if (cb.isProcessing()) return;

  const actionable = cb
    .getItems()
    .filter((it) => it.status === 'PENDING' || it.status === 'FAILED');

  if (actionable.length === 0) return;

  cb.setProcessing(true);
  try {
    for (const item of actionable) {
      // Re-check: item may have been removed while processing another
      const current = cb.getItems().find((it) => it.id === item.id);
      if (!current) continue;

      cb.setStatus(item.id, 'SYNCING');
      try {
        await processItem(item);
        cb.setStatus(item.id, 'DONE');
        // Brief delay so the DONE state is rendered before removal
        await new Promise((r) => setTimeout(r, 500));
        cb.removeItem(item.id);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Errore sconosciuto';
        cb.setStatus(item.id, 'FAILED', msg);
      }
    }
  } finally {
    cb.setProcessing(false);
  }
}
